<script lang="ts">
  import moneyFormatters from "@shared/filters/money.js";
  import CurrentUserStore from "@shared/svelte/Stores/CurrentUserStore";
  import { filter, sortBy, uniq } from "lodash";
  import { ProductCardPricesEntity } from "@shared/services/event_tracking/entities";
  import { EventTracking } from "@shared/services/event_tracking/events";
  import { soldInUnits, soldInCases, isKeg } from "@shared/svelte/ProductHelpers.js";

  import type { ProductLine, InventoryPrice } from "@shared/types/ProductLine";

  const { trackMrcEvents } = EventTracking;

  export let productLine: ProductLine;
  export let inventoryPrices: InventoryPrice[] = [];
  export let trackingEntity: ProductCardPricesEntity = null;
  export let hidePrice: boolean = false;
  export let extraClasses: string = "";

  const canShowPrices: boolean = $CurrentUserStore && !$CurrentUserStore.limited_ordering; // If no currentUser, don't show prices

  let lowPrice: number | null = null;
  let lowPriceContainer: string | null = null;
  let highPrice: number | null = null;
  let highPriceContainer: string | null = null;

  const uniquePriceCount = (priceData: InventoryPrice[]): number => {
    return uniq((priceData || []).map(p => getPrice(p))).length;
  };

  const getLowAndHighPrices = (priceData: InventoryPrice[]) => {
    const pricesForProductLine = priceData?.filter(
      price => price.product_line_id === productLine.id && (price?.unit_price || price?.case_price),
    );
    if (uniquePriceCount(pricesForProductLine) === 1) {
      lowPrice = getPrice(pricesForProductLine[0]);
      lowPriceContainer = pricesForProductLine[0].unit_container;
    } else if (pricesForProductLine?.length) {
      const sortedPrices: InventoryPrice[] = sortBy(
        filter(pricesForProductLine, getPrice),
        getPrice,
      ) as InventoryPrice[];

      lowPrice = getPrice(sortedPrices[0]);
      lowPriceContainer = sortedPrices[0].unit_container;
      highPrice = getPrice(sortedPrices[sortedPrices.length - 1]);
      highPriceContainer = sortedPrices[sortedPrices.length - 1].unit_container;
    }
  };

  const getPrice = (priceData: InventoryPrice): number => {
    if (!priceData) {
      return null;
    }

    if ((soldInUnits(priceData, productLine) || isKeg(priceData)) && priceData.unit_price) {
      return priceData.unit_price;
    }

    if (soldInCases(priceData, productLine) && priceData.case_price) {
      return priceData.case_price / (priceData.container_case_size || 1);
    }

    return null;
  };

  const formatPrice = (price: number, unitContainer: string | null): string => {
    const moneyAmount = moneyFormatters.formatWithDecimal(price);

    if (!unitContainer) {
      return moneyAmount;
    }

    return `${moneyAmount} (${unitContainer})`;
  };

  const calculateDisplayedPrices = () => {
    if (!highPrice) {
      return formatPrice(lowPrice, lowPriceContainer);
    }

    return `${formatPrice(lowPrice, lowPriceContainer)} - ${formatPrice(
      highPrice,
      highPriceContainer,
    )}`;
  };

  $: {
    getLowAndHighPrices(inventoryPrices);

    if (lowPrice && (!trackingEntity || lowPrice !== trackingEntity.low_price_amount)) {
      trackingEntity = new ProductCardPricesEntity({
        product_line_id: productLine.id,
        low_price_amount: lowPrice,
        low_price_container: lowPriceContainer,
        high_price_amount: highPrice,
        high_price_container: highPriceContainer,
      });
    }
  }
</script>

{#if !hidePrice}
  {#if canShowPrices && lowPrice}
    {#key lowPrice}
      <div data-testid="product-line-prices" use:trackMrcEvents={[[trackingEntity]]}>
        <div
          data-testid={`product-line-${productLine.id}-prices`}
          style="color:#1b1b1b;"
          class={extraClasses}
        >
          {calculateDisplayedPrices()}
        </div>
      </div>
    {/key}
  {:else}
    <div data-testid="product-line-no-price" style="color:#1b1b1b;">Price not available</div>
  {/if}
{/if}
